import React, { useState, useEffect } from 'react';
import { 
  Vote, 
  CheckCircle, 
  XCircle, 
  MinusCircle,
  Clock,
  Users,
  AlertCircle,
  X,
  User,
  Building2,
  Percent,
  TrendingUp,
  Eye,
  EyeOff
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/notifications/ToastProvider';

const LiveVotingModal = ({ motion, isOpen, onClose, onVoteSubmitted }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [voting, setVoting] = useState(false);
  const [votes, setVotes] = useState([]);
  const [myVote, setMyVote] = useState(null);
  const [votingStats, setVotingStats] = useState({
    yesVotes: 0,
    noVotes: 0,
    abstainVotes: 0,
    totalVotes: 0,
    totalWeight: 0,
    yesWeight: 0,
    noWeight: 0,
    abstainWeight: 0
  });
  const [showVoteDetails, setShowVoteDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && motion) {
      loadVotingData();
      // Set up real-time subscription for votes
      const subscription = masterSupabase
        .channel(`motion_votes_${motion.id}`)
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'hoa_votes',
            filter: `motion_id=eq.${motion.id}`
          }, 
          (payload) => {
            console.log('Real-time vote update:', payload);
            loadVotingData();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [isOpen, motion]);

  const loadVotingData = async () => {
    if (!motion) return;
    
    setLoading(true);
    try {
      // Load all votes for this motion
      const { data: votesData, error: votesError } = await masterSupabase
        .from('hoa_votes')
        .select(`
          *,
          hoa_memberships(
            id,
            ownership_share_ppm,
            hoa_members(id, full_name),
            hoa_units(label)
          )
        `)
        .eq('motion_id', motion.id);

      if (votesError) {
        console.error('Error loading votes:', votesError);
        addToast('Fehler beim Laden der Stimmen', 'error');
        return;
      }

      setVotes(votesData || []);

      // Find current user's vote
      const userVote = votesData?.find(vote => 
        vote.hoa_memberships?.hoa_members?.id === user?.id
      );
      setMyVote(userVote?.vote || null);

      // Calculate voting statistics
      const stats = calculateVotingStats(votesData || []);
      setVotingStats(stats);

    } catch (error) {
      console.error('Error loading voting data:', error);
      addToast('Fehler beim Laden der Abstimmungsdaten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateVotingStats = (votesData) => {
    const stats = {
      yesVotes: 0,
      noVotes: 0,
      abstainVotes: 0,
      totalVotes: votesData.length,
      totalWeight: 0,
      yesWeight: 0,
      noWeight: 0,
      abstainWeight: 0
    };

    votesData.forEach(vote => {
      const weight = vote.weight_ppm || vote.hoa_memberships?.ownership_share_ppm || 0;
      stats.totalWeight += weight;

      switch (vote.vote) {
        case 'yes':
          stats.yesVotes++;
          stats.yesWeight += weight;
          break;
        case 'no':
          stats.noVotes++;
          stats.noWeight += weight;
          break;
        case 'abstain':
          stats.abstainVotes++;
          stats.abstainWeight += weight;
          break;
      }
    });

    return stats;
  };

  const submitVote = async (voteType) => {
    if (!motion || !user) return;

    setVoting(true);
    try {
      // First, get user's membership for this community
      const { data: membershipData, error: membershipError } = await masterSupabase
        .from('hoa_memberships')
        .select('id, ownership_share_ppm')
        .eq('hoa_members.id', user.id)
        .eq('community_id', motion.community_id)
        .single();

      if (membershipError) {
        console.error('Error finding membership:', membershipError);
        addToast('Mitgliedschaft nicht gefunden', 'error');
        return;
      }

      // Check if user already voted
      const { data: existingVote, error: checkError } = await masterSupabase
        .from('hoa_votes')
        .select('id')
        .eq('motion_id', motion.id)
        .eq('membership_id', membershipData.id)
        .single();

      if (existingVote) {
        // Update existing vote
        const { error: updateError } = await masterSupabase
          .from('hoa_votes')
          .update({
            vote: voteType,
            voted_at: new Date().toISOString(),
            weight_ppm: membershipData.ownership_share_ppm
          })
          .eq('id', existingVote.id);

        if (updateError) {
          console.error('Error updating vote:', updateError);
          addToast('Fehler beim Aktualisieren der Stimme', 'error');
          return;
        }
      } else {
        // Create new vote
        const { error: insertError } = await masterSupabase
          .from('hoa_votes')
          .insert({
            motion_id: motion.id,
            membership_id: membershipData.id,
            vote: voteType,
            voted_at: new Date().toISOString(),
            weight_ppm: membershipData.ownership_share_ppm
          });

        if (insertError) {
          console.error('Error inserting vote:', insertError);
          addToast('Fehler beim Abgeben der Stimme', 'error');
          return;
        }
      }

      setMyVote(voteType);
      addToast(`Stimme "${getVoteLabel(voteType)}" erfolgreich abgegeben`, 'success');
      
      // Reload voting data
      await loadVotingData();
      
      // Notify parent component
      if (onVoteSubmitted) {
        onVoteSubmitted(motion.id, voteType);
      }

    } catch (error) {
      console.error('Error submitting vote:', error);
      addToast('Fehler beim Abstimmen', 'error');
    } finally {
      setVoting(false);
    }
  };

  const getVoteLabel = (voteType) => {
    switch (voteType) {
      case 'yes': return 'Ja';
      case 'no': return 'Nein';
      case 'abstain': return 'Enthaltung';
      default: return voteType;
    }
  };

  const getVoteIcon = (voteType) => {
    switch (voteType) {
      case 'yes': return CheckCircle;
      case 'no': return XCircle;
      case 'abstain': return MinusCircle;
      default: return Vote;
    }
  };

  const getVoteColor = (voteType) => {
    switch (voteType) {
      case 'yes': return 'text-green-600 bg-green-100 border-green-200';
      case 'no': return 'text-red-600 bg-red-100 border-red-200';
      case 'abstain': return 'text-gray-600 bg-gray-100 border-gray-200';
      default: return 'text-blue-600 bg-blue-100 border-blue-200';
    }
  };

  const calculatePercentage = (weight, totalWeight) => {
    if (totalWeight === 0) return 0;
    return Math.round((weight / totalWeight) * 100);
  };

  const getRequiredMajority = () => {
    switch (motion.majority_type) {
      case 'simple': return 50;
      case 'two_thirds': return 67;
      case 'three_quarters': return 75;
      case 'unanimity': return 100;
      default: return 50;
    }
  };

  const isQuorumMet = () => {
    const requiredMajority = getRequiredMajority();
    const yesPercentage = calculatePercentage(votingStats.yesWeight, votingStats.totalWeight);
    return yesPercentage >= requiredMajority;
  };

  if (!isOpen || !motion) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <Vote className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Live-Abstimmung</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {motion.hoa_meetings?.hoa_communities?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Motion Details */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {motion.title}
          </h3>
          {motion.description && (
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {motion.description}
            </p>
          )}
          
          {/* Voting Status */}
          <div className="flex items-center space-x-4 text-sm">
            <span className={`px-3 py-1 rounded-full font-medium border ${
              motion.status === 'open' ? 'bg-orange-100 text-orange-800 border-orange-200' :
              motion.status === 'passed' ? 'bg-green-100 text-green-800 border-green-200' :
              'bg-red-100 text-red-800 border-red-200'
            }`}>
              {motion.status === 'open' ? 'Abstimmung läuft' : 
               motion.status === 'passed' ? 'Angenommen' : 'Abgelehnt'}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {getRequiredMajority()}% Mehrheit erforderlich
            </span>
            {isQuorumMet() && (
              <span className="flex items-center space-x-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>Mehrheit erreicht</span>
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Lade Abstimmungsdaten...</p>
          </div>
        ) : (
          <>
            {/* Voting Progress */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Abstimmungsfortschritt</h4>
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <Users className="w-4 h-4" />
                  <span>{votingStats.totalVotes} Stimmen</span>
                </div>
              </div>

              {/* Progress Bars */}
              <div className="space-y-3">
                {/* Yes Votes */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-green-600 flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4" />
                      <span>Ja ({votingStats.yesVotes})</span>
                    </span>
                    <span className="text-sm text-gray-500">
                      {calculatePercentage(votingStats.yesWeight, votingStats.totalWeight)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculatePercentage(votingStats.yesWeight, votingStats.totalWeight)}%` }}
                    ></div>
                  </div>
                </div>

                {/* No Votes */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-red-600 flex items-center space-x-1">
                      <XCircle className="w-4 h-4" />
                      <span>Nein ({votingStats.noVotes})</span>
                    </span>
                    <span className="text-sm text-gray-500">
                      {calculatePercentage(votingStats.noWeight, votingStats.totalWeight)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculatePercentage(votingStats.noWeight, votingStats.totalWeight)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Abstain Votes */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600 flex items-center space-x-1">
                      <MinusCircle className="w-4 h-4" />
                      <span>Enthaltung ({votingStats.abstainVotes})</span>
                    </span>
                    <span className="text-sm text-gray-500">
                      {calculatePercentage(votingStats.abstainWeight, votingStats.totalWeight)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gray-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculatePercentage(votingStats.abstainWeight, votingStats.totalWeight)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Required Majority Line */}
              <div className="mt-4 relative">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>0%</span>
                  <span className="absolute" style={{ left: `${getRequiredMajority()}%`, transform: 'translateX(-50%)' }}>
                    <div className="flex flex-col items-center">
                      <div className="w-px h-4 bg-blue-600"></div>
                      <span className="text-blue-600 font-medium">{getRequiredMajority()}%</span>
                    </div>
                  </span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            {/* My Vote Status */}
            {myVote && (
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${getVoteColor(myVote)}`}>
                    {React.createElement(getVoteIcon(myVote), { className: "w-4 h-4" })}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Ihre Stimme: {getVoteLabel(myVote)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Sie können Ihre Stimme jederzeit ändern
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Voting Buttons */}
            {motion.status === 'open' && (
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                  {myVote ? 'Stimme ändern' : 'Abstimmen'}
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => submitVote('yes')}
                    disabled={voting}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                      myVote === 'yes' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                    } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>Ja</span>
                  </button>
                  
                  <button
                    onClick={() => submitVote('no')}
                    disabled={voting}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                      myVote === 'no' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-200'
                    } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <XCircle className="w-5 h-5" />
                    <span>Nein</span>
                  </button>
                  
                  <button
                    onClick={() => submitVote('abstain')}
                    disabled={voting}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                      myVote === 'abstain' 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <MinusCircle className="w-5 h-5" />
                    <span>Enthalten</span>
                  </button>
                </div>
              </div>
            )}

            {/* Vote Details */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Abstimmungsdetails</h4>
                <button
                  onClick={() => setShowVoteDetails(!showVoteDetails)}
                  className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  {showVoteDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  <span>{showVoteDetails ? 'Verbergen' : 'Details anzeigen'}</span>
                </button>
              </div>

              {showVoteDetails && (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {votes.map((vote) => (
                    <div key={vote.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border ${getVoteColor(vote.vote)}`}>
                          {React.createElement(getVoteIcon(vote.vote), { className: "w-3 h-3" })}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {vote.hoa_memberships?.hoa_members?.full_name || 'Unbekannt'}
                          </p>
                          {vote.hoa_memberships?.hoa_units?.label && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {vote.hoa_memberships.hoa_units.label}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {getVoteLabel(vote.vote)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {((vote.weight_ppm || 0) / 10000).toFixed(2)}% Anteil
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveVotingModal;

